/* Grace Spring — delivery area checker + map (sections/delivery.liquid).

   Which ZIPs are served, and at what fee, comes from the section settings
   (data-* on [data-delivery-zone]), so the list is edited in the theme editor.
   Three groups: free delivery, standard fee, and New York. New York pays its
   own flat fee (data-fee-ny) or, when that is empty, fee + surcharge. A ZIP in
   the free list wins over the other two, so moving a town to free delivery
   only means adding it there.
   Geography comes from grace-delivery-map.json (Content -> Files, loaded via
   the section's "Map data file" setting), built from US Census county
   shapes and a ZIP database: county outlines already projected into the SVG's
   coordinate box, every ZIP in the prefixes listed in `full` (all of NJ, and
   NYC + Long Island + Westchester) as "zip,cityIndex[,x,y]" rows, and the
   3-digit prefix -> state table used to tell a typo from an out-of-area ZIP.

   The JSON is fetched only when the section comes near the viewport (or the
   input is focused), so the homepage never pays for it up front. */
(function () {
  'use strict';

  var SVG = 'http://www.w3.org/2000/svg';
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var STATES = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado',
    CT: 'Connecticut', DE: 'Delaware', DC: 'Washington, DC', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii',
    ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
    ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi',
    MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
    NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma',
    OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
    TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
    WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming', PR: 'Puerto Rico', VI: 'U.S. Virgin Islands',
    GU: 'Guam', AS: 'American Samoa', MP: 'Northern Mariana Islands', AA: 'Armed Forces (AA)',
    AE: 'Armed Forces (AE)', AP: 'Armed Forces (AP)', FM: 'Micronesia', MH: 'Marshall Islands', PW: 'Palau'
  };

  function zipSet(text) {
    var set = new Set();
    (text || '').split(/[^0-9]+/).forEach(function (z) { if (z.length === 5) set.add(z); });
    return set;
  }

  function fill(template, vars) {
    return (template || '').replace(/\{(\w+)\}/g, function (m, k) {
      return vars[k] != null ? vars[k] : m;
    });
  }

  function money(n, symbol) { return symbol + n.toFixed(2); }

  // Expand "070-089,100" into a prefix -> state lookup.
  function prefixTable(pfx) {
    var table = {};
    Object.keys(pfx || {}).forEach(function (st) {
      pfx[st].split(',').forEach(function (run) {
        var ends = run.split('-');
        var from = +ends[0], to = +(ends[1] || ends[0]);
        for (var p = from; p <= to; p++) table[('00' + p).slice(-3)] = st;
      });
    });
    return table;
  }

  function el(name, attrs, parent) {
    var node = document.createElementNS(SVG, name);
    for (var k in attrs) node.setAttribute(k, attrs[k]);
    if (parent) parent.appendChild(node);
    return node;
  }

  function init(root) {
    var d = root.dataset;
    var free = zipSet(d.zipsFree);
    var std = zipSet(d.zipsStandard);
    var sur = zipSet(d.zipsSurcharge);
    // Free delivery wins: a free ZIP left in another list is not charged there.
    free.forEach(function (z) { std.delete(z); sur.delete(z); });
    var fee = parseFloat(d.fee) || 0;
    var surFee = parseFloat(d.surcharge) || 0;
    // New York's own flat fee; empty means the old fee + surcharge.
    var feeNy = parseFloat(d.feeNy);
    var nyTotal = isNaN(feeNy) ? fee + surFee : feeNy;
    var symbol = d.currency || '$';

    function served(z) { return free.has(z) || std.has(z) || sur.has(z); }

    var form = root.querySelector('[data-dz-form]');
    var input = form.querySelector('input');
    var cityLine = root.querySelector('[data-dz-city]');
    var result = root.querySelector('[data-dz-result]');
    var suggestBox = root.querySelector('[data-dz-suggest]');
    var mapBox = root.querySelector('[data-dz-map]');
    var tip = root.querySelector('[data-dz-tip]');

    var data = null, prefixes = null, full = null, zips = null, loading = null;
    var svg, view, marker, dotsLayer, home, dots = [], pending = null;

    /* ---------- data ---------- */
    function load() {
      if (loading) return loading;
      loading = fetch(d.src, { credentials: 'omit' })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then(function (json) {
          data = json;
          prefixes = prefixTable(json.pfx);
          full = prefixTable({ x: json.full });
          zips = {};
          json.zips.split(';').forEach(function (row) {
            var c = row.split(',');
            zips[c[0]] = c.length > 3
              ? [json.cities[+c[1]], +c[2], +c[3]]
              : [json.cities[+c[1]]];
          });
          if (mapBox) drawMap();
          // A ZIP typed before the data arrived gets checked now.
          if (/^\d{5}$/.test(input.value)) check(input.value, false);
        })
        .catch(function () {
          loading = null;
          if (mapBox) mapBox.classList.add('is-error');
        });
      return loading;
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        if (entries.some(function (e) { return e.isIntersecting; })) { io.disconnect(); load(); }
      }, { rootMargin: '400px 0px' });
      io.observe(root);
    } else {
      load();
    }
    input.addEventListener('focus', load, { once: true });

    /* ---------- lookup ---------- */
    // A served ZIP the database lacks still gets a sensible place name.
    function boroughFor(zip) {
      var p = zip.slice(0, 3);
      if (p === '103') return 'Staten Island';
      if (p === '104') return 'Bronx';
      if (p === '112') return 'Brooklyn';
      if (p === '111' || p === '113' || p === '114') return 'Queens';
      if (p >= '100' && p <= '102') return 'New York';
      return '';
    }

    function lookup(zip) {
      var row = zips && zips[zip];
      var info = {
        zip: zip,
        city: row ? row[0] : '',
        state: zip[0] === '0' ? 'NJ' : 'NY',
        x: row && row.length > 1 ? row[1] : null,
        y: row && row.length > 1 ? row[2] : null
      };
      if (served(zip)) {
        info.status = free.has(zip) ? 'free' : sur.has(zip) ? 'surcharge' : 'standard';
        if (!info.city) { info.city = boroughFor(zip); info.state = zip[0] === '0' ? 'NJ' : 'NY'; }
        return info;
      }
      if (row) { info.status = 'out'; return info; }
      var pre = zip.slice(0, 3);
      var st = prefixes && prefixes[pre];
      // Prefixes in `full` are listed ZIP by ZIP, so a miss there is a typo;
      // so is a prefix no state uses at all.
      if (!st || full[pre]) { info.status = 'unknown'; return info; }
      info.status = 'out';
      info.state = st;
      info.stateOnly = true;
      return info;
    }

    function place(info) {
      // Upstate ZIPs aren't in the file; "New York" alone would read as the city.
      if (info.stateOnly) return info.state === 'NY' ? 'upstate New York' : (STATES[info.state] || info.state);
      return info.city ? info.city + ', ' + info.state : info.zip;
    }

    // Served ZIPs one keystroke away: a single wrong digit or two swapped.
    function suggestions(zip) {
      var out = [];
      function consider(z) {
        if (z !== zip && served(z) && out.indexOf(z) < 0) out.push(z);
      }
      for (var i = 0; i < 5; i++) {
        for (var n = 0; n <= 9; n++) consider(zip.slice(0, i) + n + zip.slice(i + 1));
        if (i < 4) consider(zip.slice(0, i) + zip[i + 1] + zip[i] + zip.slice(i + 2));
      }
      out.sort(function (a, b) { return Math.abs(a - zip) - Math.abs(b - zip); });
      return out.slice(0, 3);
    }

    /* ---------- checker ---------- */
    function clear() {
      result.className = 'zip-result';
      result.textContent = '';
      cityLine.textContent = '';
      suggestBox.hidden = true;
      suggestBox.innerHTML = '';
      showMarker(null);
    }

    function check(zip, fromMap) {
      if (!/^\d{5}$/.test(zip)) {
        clear();
        result.className = 'zip-result no';
        result.textContent = d.msgFormat;
        return;
      }
      if (!data) {
        result.className = 'zip-result';
        result.textContent = d.msgLoading;
        load();
        return;
      }

      var info = lookup(zip);
      var vars = {
        zip: zip,
        city: place(info),
        fee: money(info.status === 'surcharge' && !isNaN(feeNy) ? feeNy : fee, symbol),
        surcharge: money(surFee, symbol),
        total: money(info.status === 'surcharge' ? nyTotal : fee, symbol)
      };
      var ok = info.status === 'free' || info.status === 'standard' || info.status === 'surcharge';

      suggestBox.hidden = true;
      suggestBox.innerHTML = '';
      cityLine.textContent = info.status === 'unknown' ? '' : '\u{1F4CD} ' + place(info) + ' · ' + zip;

      if (ok) {
        result.className = 'zip-result ok';
        var msg = info.status === 'free' ? (d.msgOkFree || d.msgOk)
          : info.status === 'surcharge' ? d.msgOkSurcharge : d.msgOk;
        result.textContent = fill(msg, vars);
      } else if (info.status === 'out') {
        result.className = 'zip-result no';
        result.textContent = fill(d.msgNo, vars);
      } else {
        result.className = 'zip-result no';
        result.textContent = fill(d.msgUnknown, vars);
        var near = suggestions(zip);
        if (near.length) {
          var label = document.createElement('span');
          label.textContent = d.msgSuggest;
          suggestBox.appendChild(label);
          near.forEach(function (z) {
            var n = lookup(z);
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'gs-dz__chip';
            b.textContent = z + ' · ' + place(n);
            b.addEventListener('click', function () { choose(z, false); });
            suggestBox.appendChild(b);
          });
          suggestBox.hidden = false;
        }
      }

      showMarker(info, ok || info.status === 'out');
      if (info.x != null && !fromMap) zoomTo(info.x, info.y, 2.4);
    }

    function choose(zip, fromMap) {
      input.value = zip;
      check(zip, fromMap);
    }

    input.addEventListener('input', function () {
      var digits = input.value.replace(/\D/g, '').slice(0, 5);
      if (digits !== input.value) input.value = digits;
      if (digits.length === 5) check(digits, false);
      else if (result.textContent) clear();
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      check(input.value.trim(), false);
    });

    /* ---------- map ---------- */
    function drawMap() {
      var w = data.w, h = data.h;
      home = { x: 0, y: 0, w: w, h: h };
      view = { x: 0, y: 0, w: w, h: h };

      svg = el('svg', {
        viewBox: '0 0 ' + w + ' ' + h,
        role: 'img',
        'aria-label': d.mapLabel,
        class: 'gs-dz__svg'
      });
      el('path', { d: data.land, class: 'gs-dz__land' }, svg);
      el('path', { d: data.coverage, class: 'gs-dz__coverage' }, svg);
      el('path', { d: data.borders, class: 'gs-dz__border' }, svg);

      dotsLayer = el('g', { 'aria-hidden': 'true' }, svg);
      function addDots(set, cls) {
        set.forEach(function (z) {
          var row = zips[z];
          if (!row || row.length < 3) return;
          el('circle', { cx: row[1], cy: row[2], r: 2.6, class: 'gs-dz__dot ' + cls }, dotsLayer);
          dots.push({ zip: z, x: row[1], y: row[2] });
        });
      }
      addDots(std, 'is-std');
      addDots(sur, 'is-sur');
      addDots(free, 'is-free'); // drawn last so they sit on top

      var labels = el('g', { class: 'gs-dz__labels', 'aria-hidden': 'true' }, svg);
      data.labels.forEach(function (l) {
        var t = el('text', { x: l[1], y: l[2], class: 'gs-dz__label is-' + l[3] }, labels);
        t.textContent = l[0];
      });

      marker = el('g', { class: 'gs-dz__marker', 'aria-hidden': 'true', visibility: 'hidden' }, svg);
      el('circle', { r: 11, class: 'gs-dz__pulse' }, marker);
      el('circle', { r: 5, class: 'gs-dz__pin' }, marker);
      var tag = el('g', { class: 'gs-dz__tag' }, marker);
      el('rect', { x: 9, y: -12, rx: 5, height: 20, class: 'gs-dz__tagbg' }, tag);
      el('text', { x: 15, y: 2, class: 'gs-dz__tagtext' }, tag);

      mapBox.querySelector('[data-dz-skeleton]') && mapBox.querySelector('[data-dz-skeleton]').remove();
      mapBox.insertBefore(svg, tip);
      bindMap();
      if (pending) showMarker(pending.info, pending.served);
    }

    function showMarker(info, served) {
      if (!marker) { pending = info ? { info: info, served: served } : null; return; }
      if (!info || info.x == null) { marker.setAttribute('visibility', 'hidden'); return; }
      marker.setAttribute('transform', 'translate(' + info.x + ',' + info.y + ')');
      marker.setAttribute('class', 'gs-dz__marker' + (served ? '' : ' is-out'));
      var text = marker.querySelector('.gs-dz__tagtext');
      text.textContent = info.city || info.zip;
      marker.setAttribute('visibility', 'visible');
      var bg = marker.querySelector('.gs-dz__tagbg');
      bg.setAttribute('width', text.getComputedTextLength() + 12);
      scaleMarker();
    }

    // Keep the pin and its label the same on-screen size at every zoom; dots
    // grow a little when zoomed (k^0.7) so a close-up still reads as a map.
    function scaleMarker() {
      if (!marker) return;
      var k = view.w / home.w;
      var t = marker.getAttribute('transform') || '';
      marker.setAttribute('transform', t.replace(/\s*scale\([^)]*\)/, '') + ' scale(' + k + ')');
      dotsLayer.style.setProperty('--dz-k', Math.pow(k, 0.7));
      // Labels hold their on-screen size too.
      svg.style.setProperty('--dz-lk', k);
    }

    function setView(v) {
      view = v;
      svg.setAttribute('viewBox', v.x + ' ' + v.y + ' ' + v.w + ' ' + v.h);
      scaleMarker();
      root.classList.toggle('is-zoomed', v.w < home.w - 0.5);
    }

    function clampView(v) {
      v.w = Math.min(home.w, Math.max(home.w / 6, v.w));
      v.h = v.w * home.h / home.w;
      v.x = Math.min(home.w - v.w, Math.max(0, v.x));
      v.y = Math.min(home.h - v.h, Math.max(0, v.y));
      return v;
    }

    var anim;
    function animateTo(target) {
      cancelAnimationFrame(anim);
      if (reduceMotion) { setView(target); return; }
      var from = { x: view.x, y: view.y, w: view.w, h: view.h };
      var start = performance.now(), dur = 520;
      (function step(now) {
        var t = Math.min(1, (now - start) / dur);
        var e = 1 - Math.pow(1 - t, 3);
        setView({
          x: from.x + (target.x - from.x) * e,
          y: from.y + (target.y - from.y) * e,
          w: from.w + (target.w - from.w) * e,
          h: from.h + (target.h - from.h) * e
        });
        if (t < 1) anim = requestAnimationFrame(step);
      })(start);
    }

    function zoomTo(x, y, factor) {
      if (!svg) return;
      var w = home.w / factor;
      animateTo(clampView({ x: x - w / 2, y: y - (w * home.h / home.w) / 2, w: w }));
    }

    function zoomBy(factor) {
      if (!svg) return;
      var cx = view.x + view.w / 2, cy = view.y + view.h / 2;
      var w = view.w / factor;
      animateTo(clampView({ x: cx - w / 2, y: cy - (w * home.h / home.w) / 2, w: w }));
    }

    root.querySelectorAll('[data-dz-zoom]').forEach(function (b) {
      b.addEventListener('click', function () {
        var z = b.dataset.dzZoom;
        if (z === 'reset') animateTo({ x: 0, y: 0, w: home.w, h: home.h });
        else zoomBy(z === 'in' ? 1.6 : 1 / 1.6);
      });
    });

    function toMap(evt) {
      var pt = svg.createSVGPoint();
      pt.x = evt.clientX; pt.y = evt.clientY;
      return pt.matrixTransform(svg.getScreenCTM().inverse());
    }

    // Nearest served dot within ~14 screen px of the pointer.
    function nearest(evt) {
      var p = toMap(evt);
      var limit = 14 * view.w / svg.getBoundingClientRect().width;
      var best = null, bestD = limit * limit;
      dots.forEach(function (dot) {
        var dx = dot.x - p.x, dy = dot.y - p.y, dd = dx * dx + dy * dy;
        if (dd < bestD) { bestD = dd; best = dot; }
      });
      return best;
    }

    function bindMap() {
      var drag = null;

      svg.addEventListener('pointerdown', function (e) {
        drag = { x: e.clientX, y: e.clientY, view: { x: view.x, y: view.y, w: view.w, h: view.h }, moved: false };
      });

      svg.addEventListener('pointermove', function (e) {
        if (drag && (drag.moved || Math.abs(e.clientX - drag.x) + Math.abs(e.clientY - drag.y) > 6)) {
          if (!drag.moved) { drag.moved = true; svg.setPointerCapture(e.pointerId); }
          var k = view.w / svg.getBoundingClientRect().width;
          setView(clampView({
            x: drag.view.x - (e.clientX - drag.x) * k,
            y: drag.view.y - (e.clientY - drag.y) * k,
            w: drag.view.w
          }));
          tip.hidden = true;
          return;
        }
        if (e.pointerType !== 'mouse') return;
        var dot = nearest(e);
        svg.classList.toggle('is-pointing', !!dot);
        if (!dot) { tip.hidden = true; return; }
        var info = lookup(dot.zip);
        tip.textContent = dot.zip + ' · ' + place(info);
        var box = mapBox.getBoundingClientRect();
        tip.style.left = (e.clientX - box.left) + 'px';
        tip.style.top = (e.clientY - box.top) + 'px';
        tip.hidden = false;
      });

      svg.addEventListener('pointerleave', function () { tip.hidden = true; });

      svg.addEventListener('pointerup', function (e) {
        var moved = drag && drag.moved;
        drag = null;
        if (moved) return;
        var dot = nearest(e);
        if (dot) choose(dot.zip, true);
      });
    }
  }

  document.querySelectorAll('[data-delivery-zone]').forEach(init);
})();
